import { supabase } from '../../shared/supabase/supabaseClient';
import {
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  withReportsCache,
} from './reportsRemoteCache';
import {
  reportsQueryScopeToRpcParams,
  type ReportsQueryScope,
} from '../domain/reportsQueryScope';
import type {
  BranchSalesByProductResponse,
  BranchSalesByProductRow,
  SalesByProductSortKey,
} from '../domain/reportsTypes';
import { mapRpcError, scopeCacheKeyParts } from './reportsRemoteShared';

/** Ventas agrupadas por producto: listado paginado y filtros (Informes > Ventas). */
export async function fetchRemoteBranchSalesByProduct(params: {
  branchId: string;
  fromIso: string;
  toIso: string;
  queryScope?: ReportsQueryScope | null;
  sort: SalesByProductSortKey;
  limit: number;
  offset: number;
}): Promise<BranchSalesByProductResponse | null> {
  const { branchId, fromIso, toIso, queryScope, sort, limit, offset } = params;
  const scope = reportsQueryScopeToRpcParams(queryScope);
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_sales_by_product',
      branchId,
      fromIso,
      toIso,
      sort,
      limit,
      offset,
      ...scopeCacheKeyParts(scope),
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_sales_by_product', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
        p_category: scope.p_category,
        p_search: scope.p_search,
        p_sort: sort,
        p_limit: limit,
        p_offset: offset,
        p_item_type: scope.p_item_type,
        p_product_id: scope.p_product_id,
        p_brand: scope.p_brand,
        p_supplier_id: scope.p_supplier_id,
        p_customer_id: scope.p_customer_id,
        p_cashier_user_id: scope.p_cashier_user_id,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_sales_by_product', mapRpcError(error));
        return null;
      }
      if (!data || typeof data !== 'object') return null;
      const j = data as Record<string, unknown>;
      const rawRows = j.rows;
      const rows: BranchSalesByProductRow[] = [];
      if (Array.isArray(rawRows)) {
        for (const raw of rawRows) {
          if (!raw || typeof raw !== 'object') continue;
          const r = raw as Record<string, unknown>;
          rows.push({
            productId: String(r.product_id ?? ''),
            label: String(r.label ?? ''),
            sku: r.sku == null || r.sku === '' ? null : String(r.sku),
            categoryId: r.category_id == null || r.category_id === '' ? null : String(r.category_id),
            itemType: r.item_type === 'service' ? 'service' : 'physical',
            unitOfMeasure: r.unit_of_measure == null ? null : String(r.unit_of_measure),
            quantity: Number(r.quantity ?? 0),
            lineTotal: Number(r.line_total ?? 0),
          });
        }
      }
      return {
        rows,
        totalCount: Number(j.total_count ?? 0),
        sumQuantity: Number(j.sum_quantity ?? 0),
        sumLineTotal: Number(j.sum_line_total ?? 0),
        uniformUnitOfMeasure: j.uniform_unit_of_measure !== false,
      };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_by_product' }
  );
}
